import React, { useContext, useState, useEffect } from 'react';
import { Alert , Card , Button, Modal, Radio, InputNumber, Tooltip, Space, Typography } from 'antd';
import { QuestionCircleOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import firebase from 'firebase/app';
import { Link } from 'react-router-dom';
import moment from 'moment';

import TrackerService from '../../utils/TrackerService';
import { UserContext } from '../../providers/UserProvider';

import { setDefaultPayout, isLocalHost, ccy } from './AccountProfileComponents';

const { Text, Paragraph } = Typography;

export const ShowCashOutDialog = ({ setLatestWithdrawalId, loading, setLoading, visible, handleOk, handleCancel, accountId, clientId, currency, clientCurrencyStr, payouts, withdrawals, balance, selectedAvailableAccountId, isAdmin, ...rest }) => {
  // Only allow payout methods that match the balance currency
  const eligiblePayouts = (payouts || []).filter(p => p && p.currency === clientCurrencyStr);
  const [selectedPayoutMethod, setSelectedPayoutMethod] = useState(() => {
    // Default to first eligible payout (index 0) if available
    if (eligiblePayouts && eligiblePayouts.length > 0) {
      return 0;
    }
    return null;
  });
  const [payoutAmount, setPayoutAmount] = useState(() => balance ? balance / 100 : 0);
  const [withdrawalError, setWithdrawalError] = useState(false);
  const [withdrawalErrorMessage, setWithdrawalErrorMessage] = useState("");
  const [withdrawalId, setWithdrawalId] = useState(null);
  const [withdrawalInfoMessage, setWithdrawalInfoMessage] = useState("");
  const [withdrawalInfoMessageTextType, setWithdrawalInfoMessageTextType] = useState(null);
  const [processingBalance, setProcessingBalance] = useState(null); // Store balance when processing starts
  const [isProcessingWithdrawal, setIsProcessingWithdrawal] = useState(false); // Track if withdrawal is being processed
  const [modalKey, setModalKey] = useState(() => `co-${Date.now()}`); // Unique key for modal to force remount
  const prevVisibleRef = React.useRef(false); // Track previous visible state

  // Clear error state when dialog opens and initialize payout amount
  useEffect(() => {
    // Only update key and reset state when transitioning from closed to open
    if (visible && !prevVisibleRef.current) {
      // Generate new key to force complete remount of modal
      setModalKey(`co-${Date.now()}`);
      
      setWithdrawalError(false);
      setWithdrawalErrorMessage("");
      setWithdrawalInfoMessage("");
      setWithdrawalInfoMessageTextType(null);
      setWithdrawalId(null);
      setProcessingBalance(null); // Clear processing balance
      setIsProcessingWithdrawal(false); // Clear processing state
      setLoading(false); // Ensure dialog is unlocked when opened
      // Ensure payout amount is properly initialized
      if (balance && balance > 0) {
        setPayoutAmount(balance / 100);
      }
      // Preselect first eligible payout if available
      if (eligiblePayouts && eligiblePayouts.length > 0) {
        setSelectedPayoutMethod(0);
      }
    }
    // Update ref to track current visible state
    prevVisibleRef.current = visible;
  }, [visible, eligiblePayouts, balance]);

  const radioStyle = {
    display: 'block',
    height: '30px',
    lineHeight: '30px',
    textAlign: 'left',
  };

  const context = useContext(UserContext);
  const user = context.user;

  const onPayoutMethodChange = (event) => {
    setSelectedPayoutMethod(event.target.value);
  };

  const onPayoutAmountChange = (number) => {
    setPayoutAmount(number);
    // Clear any previous errors when user changes amount
    if (withdrawalError) {
      setWithdrawalError(false);
      setWithdrawalErrorMessage("");
    }
  };

  const setWithdrawalInfoMessageByStatus = (status) => {
    if (status) {
      setWithdrawalInfoMessageTextType(null);
      switch (status) {
        case 'created':
          setWithdrawalInfoMessage('Creating Cash Out...');
          break;
        case 'ready':
          setWithdrawalInfoMessage('Creating Cash Out...');
          break;
        case 'successful':
          setWithdrawalInfoMessage('Successfully funded Cash Out...');
          break;
        case 'failed':
        case 'refund_failed':
        case 'incomplete':
          setWithdrawalInfoMessageTextType('danger');
          setWithdrawalInfoMessage('Something went wrong. Try again later!');
          break;
        case 'sent':
        case 'api_requested':
        case 'batch_requested':
        case 'waiting':
        case 'batched':
          setWithdrawalInfoMessageTextType('success');
          setWithdrawalInfoMessage('Cash Out on its way!');
          break;
        case 'paid':
          setWithdrawalInfoMessageTextType('success');
          setWithdrawalInfoMessage('Cash Out is paid!');
          break;
        case 'refund_requested':
          setWithdrawalInfoMessage('Cash Out is being reversed...');
          break;
        case 'refunded':
          setWithdrawalInfoMessage('Cash Out is reversed!');
          break;
        case 'refund_failed':
          setWithdrawalInfoMessageTextType('danger');
          setWithdrawalInfoMessage('Cash Out is failed to reverse. Contact support!');
          break;
        default:
          setWithdrawalInfoMessage(`Cash Out status ${status}!`);
      }
    } else {
      setWithdrawalInfoMessage('');
    }
  };

  const withdrawalAvailable = () => {
    // Escape hatch to allow users without a payout method to see they should add one now
    if (eligiblePayouts.length === 0) {
      return true;
    }

    let allOnCooldown = true;
    for (const payout of eligiblePayouts) {
      const payoutMethodCooldown = isPayoutMethodCooldown(withdrawals, payout);

      if (!payoutMethodCooldown) {
        allOnCooldown = false;
      }
    }

    return !allOnCooldown;
  };

  function getWithdrawalPeriod() {
    const defaultCooldown = {
      duration: 24,
      units: 'hours'
    };
    const localhostCooldown = {
      duration: 2,
      units: 'minutes'
    };
    return isLocalHost ? localhostCooldown : defaultCooldown;
  }

  const isPayoutMethodCooldown = (withdrawals, payout) => {
    // Admin users have no cooldown restrictions
    if (isAdmin) {
      return false;
    }

    const payoutMethodWithdrawals = withdrawals.filter((withdrawal) => {
      return withdrawal.payoutId === payout && payout.id && withdrawal.status !== "failed";
    });

    const sortedWithdrawals = payoutMethodWithdrawals.sort((a, b) => {
      return (a.withdrawal.createdAt < b.withdrawal.createdAt) ? 1 : ((b.withdrawal.createdAt < a.withdrawal.createdAt ? -1 : 0));
    });

    const latestWithdrawal = sortedWithdrawals.length > 0 ? sortedWithdrawals[0].withdrawal : null;

    if (latestWithdrawal && latestWithdrawal.createdAt) {
      const now = moment().utc();
      const withdrawalTime = moment(latestWithdrawal.createdAt.toMillis()).utc();

      const withdrawalPeriod = getWithdrawalPeriod();
      const nextWithdrawalTime = withdrawalTime.add(withdrawalPeriod.duration, withdrawalPeriod.units);

      return nextWithdrawalTime.isAfter(now) ? true : false;
      }
      return false;
    };

  const withdrawalsCooldown = () => {
    // Admin users have no cooldown restrictions
    if (isAdmin) {
      return null;
    }

    // Filter out failed withdrawals - only consider successful ones for cooldown
    const successfulWithdrawals = withdrawals.filter(withdrawal => withdrawal.status !== "failed");
    
    const sortedWithdrawals = successfulWithdrawals.sort((a, b) => {
      return (a.createdAt < b.createdAt) ? 1 : ((b.createdAt < a.createdAt ? -1 : 0));
    });

    const latestWithdrawal = sortedWithdrawals.length > 0 ? sortedWithdrawals[0] : null;

    if (latestWithdrawal && latestWithdrawal.createdAt) {
      const now = moment().utc();
      const withdrawalTime = moment(latestWithdrawal.createdAt.toMillis()).utc();

      const withdrawalPeriod = getWithdrawalPeriod();
      const nextWithdrawalTime = withdrawalTime.add(withdrawalPeriod.duration, withdrawalPeriod.units);

      if (nextWithdrawalTime.isAfter(now)) {
        const diffMinutes = nextWithdrawalTime.diff(now, "minutes");
        return diffMinutes >= 60 ? `${Math.floor(diffMinutes / 60)} hours` : `${diffMinutes} minutes`;
      }
    }

    return null;
  };

  const currencyMappings = {
    EUR: '€',
    GBP: '£',
    USD: '$',
  };

  const mapCurrencySafely = (ccy) => {
    return currencyMappings[ccy] ? currencyMappings[ccy] : '';
  };

  const tooltopForRadioButton = (payout) => {
    let isQuestion = true;

    let displayName = '';
    displayName = payout.holderName ? `${displayName}${payout.holderName.substring(0, 32)} ` : `${displayName}`;

    let accountNumber = payout.accountNumber ? `**${payout.accountNumber.slice(-8)}` : '';
    accountNumber = payout.iban ? `${payout.iban.substring(0, 8)}**${payout.iban.slice(-4)}` : `${accountNumber}`;

    let currencyLabel = payout.currency ? `(${mapCurrencySafely(payout.currency)}) ` : '';
    accountNumber = `${currencyLabel} ${accountNumber}`;


    let tooltipTitle = 
      <Space direction='vertical'>
        <>{displayName}</>
        <>{accountNumber}</>
      </Space>;

    if(isPayoutMethodCooldown(withdrawals, payout)) {
      isQuestion = false;
      tooltipTitle = "Has already been used this period";
    }

    // Currency mismatch is filtered out; no tooltip needed

    return <Tooltip title={tooltipTitle}>
          { isQuestion ? <QuestionCircleOutlined /> : <ExclamationCircleOutlined />}
      </Tooltip>;
  };

  const payoutOptions = eligiblePayouts.map((payout, i) => 
    <Radio 
      style={radioStyle} 
      value={i} 
      key={i} 
      disabled={isPayoutMethodCooldown(withdrawals, payout)}
    >
        {payout.displayName ? `${payout.displayName.substring(0,16)} ` : '' }
        {tooltopForRadioButton(payout)}
    </Radio>
  );

  const setWithdrawalObject = async () => {
    if (!withdrawalAvailable()) {
      handleCancel();
      return;
    }
    
    // Clear any previous errors when starting processing
    setWithdrawalError(false);
    setWithdrawalErrorMessage("");
    
    // Store the current balance to prevent UI updates during processing
    setProcessingBalance(balance);
    setIsProcessingWithdrawal(true); // Mark as processing
    
    setLoading(true);
    setWithdrawalInfoMessage("Preparing cash out...");

    const payoutAmountCents = Math.round((payoutAmount || 0) * 100);

    if (payoutAmountCents <= 0) {
      setWithdrawalError(true);
      setWithdrawalErrorMessage(`Error: Please enter a valid amount greater than 0`);
      setLoading(false);
      setProcessingBalance(null); // Clear processing balance on error
      setIsProcessingWithdrawal(false); // Clear processing state on error
      return;
    }

    if (selectedPayoutMethod === null || selectedPayoutMethod === undefined) {
      setWithdrawalError(true);
      setWithdrawalErrorMessage(`Error: Please select a payout method`);
      setLoading(false);
      setProcessingBalance(null); // Clear processing balance on error
      setIsProcessingWithdrawal(false); // Clear processing state on error
      return;
    }

    const selectedPayout = eligiblePayouts[selectedPayoutMethod];
    if (!selectedPayout) {
      setWithdrawalError(true);
      setWithdrawalErrorMessage(`Error: Selected payout method not found`);
      setLoading(false);
      setProcessingBalance(null); // Clear processing balance on error
      setIsProcessingWithdrawal(false); // Clear processing state on error
      return;
    }
    
    const payoutId = selectedPayout.id;

    if (selectedPayout.currency !== clientCurrencyStr) {
      setWithdrawalError(true);
      setWithdrawalErrorMessage(`Error: Bank account currency (${ccy[selectedPayout.currency]}) does not match funds being withdrawn (${currency}). select another payout method or add a new one in Setup Payouts`);
      setLoading(false);
      setProcessingBalance(null); // Clear processing balance on error
      setIsProcessingWithdrawal(false); // Clear processing state on error
      return;
    }

    if (payoutAmountCents < minimumWithdrawalAmount) {
      setWithdrawalError(true);
      setWithdrawalErrorMessage(`Error: Minimum withdrawal is ${currency}${minimumWithdrawalAmountString}. You tried to withdraw ${currency}${(payoutAmountCents / 100).toFixed(2)}`);
      setLoading(false);
      setProcessingBalance(null); // Clear processing balance on error
      setIsProcessingWithdrawal(false); // Clear processing state on error
      return;
    }

    const withdrawalObjV2 = {
      payoutId: payoutId,
      payoutAmountCents: +payoutAmountCents,
      accountId: accountId,
      currency: clientCurrencyStr,
      version: '2',
      initiatorUserId: user.uid,
    };

    const payoutAmountDecimal = ((payoutAmountCents * 1)/100).toFixed(2);

    const db = firebase.firestore();
    
    try {
      const latestWithdrawalSnapshot = await db.collection('clients').doc(clientId).collection('withdrawals').add(withdrawalObjV2);
      setLatestWithdrawalId(latestWithdrawalSnapshot.id);
      setWithdrawalId(latestWithdrawalSnapshot.id);
      
      // Don't set loading to false yet - wait for Cloud Function to process
      // Don't call handleOk() yet - wait for status update

      const isTest = isLocalHost;

      const tracker = new TrackerService({
        isTest,
      });

      tracker.mixpanelTrack("payout_requested", {
        distinct_id: clientId,
        clientId,
        payoutId,
        payoutAmountCents,
        amount: payoutAmountDecimal,
        accountId,
        currency: clientCurrencyStr,
        initiatorUserId: withdrawalObjV2.initiatorUserId
      });

      if (!isTest) {
        tracker.googleTrack(
          "payout_requested",
          {
            clientId,
            payoutId,
            payoutAmountCents,
            amount: payoutAmountDecimal,
            accountId,
            currency: clientCurrencyStr,
            initiatorUserId: withdrawalObjV2.initiatorUserId
          }
        );
      }

      // The useEffect will handle the success/failure based on status updates
    } catch (error) {
      console.error('Withdrawal creation failed:', error);
      setLoading(false);
      setProcessingBalance(null); // Clear processing balance on error
      setIsProcessingWithdrawal(false); // Clear processing state on error
      
      // Handle specific error cases
      if (error.code === 'permission-denied') {
        setWithdrawalError(true);
        setWithdrawalErrorMessage('You do not have permission to create this withdrawal. Please contact support.');
      } else if (error.message && error.message.includes('rate limit')) {
        const withdrawalPeriod = getWithdrawalPeriod();
        setWithdrawalError(true);
        setWithdrawalErrorMessage(`Cash out is limited to one per ${withdrawalPeriod.duration} ${withdrawalPeriod.units} per bank account. Please wait a moment before trying again.`);
      } else if (error.message && error.message.includes('insufficient funds')) {
        setWithdrawalError(true);
        setWithdrawalErrorMessage('Insufficient funds available for withdrawal. Please check your balance.');
      } else {
        setWithdrawalError(true);
        setWithdrawalErrorMessage(`Failed to process withdrawal. ${error.message || 'Please try again or contact support.'}`);
      }
    }
  };

  const trackWithdrawalStatusChange = (withdrawal) => {
    const { withdrawalId, status, payoutId, clientId, accountId, currency, payoutAmountCents, is_test, initiatorUserId, type, provider } = withdrawal || {};
    const amount = (payoutAmountCents * 1)/100;
    const amountText = amount.toFixed(2);
    const isTest = isLocalHost || !!is_test;
    const payoutType = type || 'batch';
    const payoutProvider = provider || 'strike';

    const isValidWithdrawalStatus = withdrawal && status && status.length > 0 && amount && amount > 0 && withdrawalId && withdrawalId.length > 0;

    if (!isValidWithdrawalStatus) {
      return;
    }

    const tracker = new TrackerService({
      isTest,
    });

    const eventName = `withdrawal_${status}`;

    tracker.mixpanelTrack(eventName, {
      distinct_id: clientId,
      clientId,
      payoutId,
      payoutType,
      payoutProvider,
      accountId,
      withdrawalId,
      amount: amountText,
      currency,
      initiatorUserId,
      status,
    });

    if (!isTest) {
      tracker.googleTrack(
        eventName,
        {
          clientId,
          payoutId,
          payoutType,
          payoutProvider,
          accountId,
          withdrawalId,
          amount,
          currency,
          initiatorUserId,
          status,
        }
      );
    }
  }

  const minimumWithdrawalAmount = isAdmin ? 0 : 500;
  const minimumWithdrawalAmountString = (minimumWithdrawalAmount / 100).toFixed(2);

  const withdrawalPeriod = getWithdrawalPeriod();
  // Terminal states that unlock the UI: 'failed', 'api_requested', 'sent', 'batch_requested', 'paid'
  // All other states keep the dialog locked

  const db = firebase.firestore();
  
  // TODO: DOUBLE WITHDRAWAL STATUS UPDATE
  useEffect(() => {
    if (clientId && withdrawalId) {
      const latestWithdrawalObserver = db.collection('clients').doc(clientId).collection('withdrawals').doc(withdrawalId)
        .onSnapshot((snapshot) => {
          const snapshotData = snapshot.data();
          setWithdrawalInfoMessageByStatus(snapshotData.status);
          trackWithdrawalStatusChange(snapshotData);
          
          // ONLY unlock on explicit terminal states: 'failed', 'api_requested', 'sent', 'batch_requested', 'paid'
          // Ignore ALL other intermediate states
          if (snapshotData.status === 'failed' || 
              snapshotData.status === 'api_requested' || 
              snapshotData.status === 'sent' || 
              snapshotData.status === 'batch_requested' || 
              snapshotData.status === 'paid') {
            latestWithdrawalObserver();
            setWithdrawalId(null);
            // Don't clear processing state yet - keep green locked display during celebration
            
            if (snapshotData.status === 'failed') {
              setLoading(false);
              setProcessingBalance(null);
              setIsProcessingWithdrawal(false);
              
              // Show error message for failed withdrawals
            let errorMessage = snapshotData.statusReason || snapshotData.errorMessage || snapshotData.info || 'Withdrawal failed. Please try again or contact support.';
            
            // Provide more helpful messages for common error cases
            if (errorMessage.includes('24 hours') || errorMessage.includes('2 minutes') || errorMessage.includes('rate limit') || errorMessage.includes('Previous withdrawal less than')) {
              const withdrawalPeriod = getWithdrawalPeriod();
              errorMessage = `Cash out is limited to one per ${withdrawalPeriod.duration} ${withdrawalPeriod.units} per bank account. Please wait a moment before trying again.`;
            } else if (errorMessage.includes('insufficient') || errorMessage.includes('balance') || errorMessage.includes('Insufficient Funds')) {
              errorMessage = 'Insufficient funds available for withdrawal. Please check your balance.';
            } else if (errorMessage.includes('minimum') || errorMessage.includes('less than 5.00')) {
              errorMessage = 'Withdrawal amount is below the minimum required. Please enter a larger amount.';
            } else if (errorMessage.includes('Incorrect parameters')) {
              errorMessage = 'Invalid withdrawal request. Please try again.';
            }
            
            setWithdrawalError(true);
            setWithdrawalErrorMessage(errorMessage);
            
            // Clear info messages
            setWithdrawalInfoMessageByStatus(null);
            setWithdrawalInfoMessageTextType(null);
          } else {
            // Success case - show celebration and close dialog
            setWithdrawalInfoMessageTextType('success');
            setWithdrawalInfoMessage('✅ Cash Out completed successfully!');
            // Keep processing state active during celebration
            setTimeout(() => { 
              setLoading(false);
              setWithdrawalError(false);
              setWithdrawalErrorMessage('');
              setWithdrawalInfoMessageByStatus(null);
              setWithdrawalInfoMessageTextType(null);
              // Clear processing state only when dialog closes
              setProcessingBalance(null);
              setIsProcessingWithdrawal(false);
              handleCancel();
            }, 2400);
          }
        }
      });
    }
  }, [withdrawalId]);

  return (
    <Modal 
      key={modalKey}
      title="Cash Out Available Balance" 
      visible={visible} 
      onOk={setWithdrawalObject} 
      onCancel={handleCancel} 
      cancelButtonProps={{ disabled: loading }} 
      okButtonProps={{ disabled: loading || !withdrawalAvailable() || (payouts && payouts.length === 0) }}
      destroyOnClose={true}
    >
      <div style={{ textAlign: 'center' }}>
        {withdrawalError && <Alert message={withdrawalErrorMessage} type="error" showIcon />}

        {withdrawalAvailable()
          ? (
            <>
              <Space direction='vertical' size={'small'}>
                <Text >Allow for up to <strong>3 business days</strong> for money to arrive in your chosen bank account.</Text>
              </Space>
              
              <p></p>

              <Card actions={payouts.length > 0 ? ['Tap on `?` for account details'] : []}>
                <p>Select Payout Method</p>

                <Radio.Group onChange={onPayoutMethodChange} value={selectedPayoutMethod}>
                  {payoutOptions}
                  <p></p>
                  <Button 
                    type='primary'
                    onClick={() => {
                      const basePath = isAdmin ? '/admin/clients' : '/client/manage';
                      window.location.href = `${basePath}/${clientId}/bank`;
                    }}
                  >
                    {payouts.length > 0 ? 'Add New Payout' : 'Add Payout Method'}
                  </Button>
                </Radio.Group>

              </Card>

              <Card>
                <p>{isProcessingWithdrawal ? "Processing Amount" : "Enter Amount"}</p>

                {balance >= minimumWithdrawalAmount
                  ?
                  isProcessingWithdrawal 
                    ? <div style={{ padding: '8px 12px', border: '1px solid #52c41a', borderRadius: '6px', backgroundColor: '#f6ffed', color: '#52c41a', fontWeight: 'bold' }}>
                        {currency}{(payoutAmount || 0).toFixed(2)}
                      </div>
                    : <InputNumber
                        value={payoutAmount}
                        max={balance / 100}
                        precision={2}
                        onChange={onPayoutAmountChange} />
                  :
                  !isProcessingWithdrawal && <p>{`Minimum payout amount is ${currency}${minimumWithdrawalAmountString}`}</p>
                  }

                  {!isProcessingWithdrawal && <p>Your available balance: {currency}{((processingBalance || balance) / 100).toFixed(2)}</p>}

              </Card>

              <Space direction='vertical' size={0}>
                <Text>
                  {isAdmin 
                    ? "Admin users have no withdrawal cooldown restrictions" 
                    : `Cash Out is limited to one per ${withdrawalPeriod.duration} ${withdrawalPeriod.units} per account`
                  }
                </Text>
                {withdrawalInfoMessage && withdrawalInfoMessage.length > 0 &&
                  <Text type={withdrawalInfoMessageTextType}>{withdrawalInfoMessage}</Text>
                }
              </Space>        
            </>
          )
          : (
            <Card>
              <p>{`Withdrawals are limited to one per ${withdrawalPeriod.duration} ${withdrawalPeriod.units}`}</p>
              <p>{`You can withdraw again in ${withdrawalsCooldown()}`}</p>
            </Card>
          )}
      </div>
    </Modal>
  );
};
